import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { automatedReportingAsyncActions } from './automatedReportingActions'
import {
  ScheduledReport,
  ScheduledReportRun,
} from './automatedReportingTypes'

export interface AutomatedReportingState {
  scheduledReports: ScheduledReport[]
  scheduledReportsLoading: boolean
  scheduledReportsError: string | null
  scheduledReportRuns: ScheduledReportRun[]
  scheduledReportRunsLoading: boolean
  scheduledReportRunsError: string | null
  scheduledReportCreateLoading: boolean
  scheduledReportCreateError: string | null
  scheduledReportCreateSuccess: boolean
  scheduledReportUpdateLoading: boolean
  scheduledReportUpdateError: string | null
  scheduledReportUpdateSuccess: boolean
}

const initialState: AutomatedReportingState = {
  scheduledReports: [],
  scheduledReportsLoading: false,
  scheduledReportsError: null,
  scheduledReportRuns: [],
  scheduledReportRunsLoading: false,
  scheduledReportRunsError: null,
  scheduledReportCreateLoading: false,
  scheduledReportCreateError: null,
  scheduledReportCreateSuccess: false,
  scheduledReportUpdateLoading: false,
  scheduledReportUpdateError: null,
  scheduledReportUpdateSuccess: false,
}

type AutomatedReportingErrorAction = PayloadAction<
  { message: string } | undefined
> & {
  error?: { message?: string }
}

const getAutomatedReportingErrorMessage = (
  action: AutomatedReportingErrorAction
) =>
  action.payload?.message || action.error?.message || 'Unable to load data.'

const sortScheduledReports = (scheduledReports: ScheduledReport[]) =>
  [...scheduledReports].sort(
    (a, b) =>
      a.nextReportDate.localeCompare(b.nextReportDate) ||
      a.name.localeCompare(b.name)
  )

const automatedReportingSlice = createSlice({
  name: 'AutomatedReporting',
  initialState,
  reducers: {
    setScheduledReportsLoading(state, action: PayloadAction<boolean>) {
      state.scheduledReportsLoading = action.payload
    },
    getScheduledReportsSuccess(
      state,
      action: PayloadAction<ScheduledReport[]>
    ) {
      state.scheduledReports = action.payload
      state.scheduledReportsLoading = false
      state.scheduledReportsError = null
    },
    getScheduledReportsFailed(
      state,
      action: AutomatedReportingErrorAction
    ) {
      state.scheduledReportsLoading = false
      state.scheduledReportsError = getAutomatedReportingErrorMessage(action)
    },
    setScheduledReportRunsLoading(state, action: PayloadAction<boolean>) {
      state.scheduledReportRunsLoading = action.payload
    },
    getScheduledReportRunsSuccess(
      state,
      action: PayloadAction<ScheduledReportRun[]>
    ) {
      state.scheduledReportRuns = action.payload
      state.scheduledReportRunsLoading = false
      state.scheduledReportRunsError = null
    },
    getScheduledReportRunsFailed(
      state,
      action: AutomatedReportingErrorAction
    ) {
      state.scheduledReportRunsLoading = false
      state.scheduledReportRunsError = getAutomatedReportingErrorMessage(action)
    },
    setScheduledReportCreateLoading(state, action: PayloadAction<boolean>) {
      state.scheduledReportCreateLoading = action.payload
    },
    createScheduledReportSuccess(state, action: PayloadAction<ScheduledReport>) {
      state.scheduledReports = sortScheduledReports([
        ...state.scheduledReports,
        action.payload,
      ])
      state.scheduledReportCreateLoading = false
      state.scheduledReportCreateError = null
      state.scheduledReportCreateSuccess = true
    },
    updateScheduledReportSuccess(state, action: PayloadAction<ScheduledReport>) {
      state.scheduledReports = sortScheduledReports(
        state.scheduledReports.map((scheduledReport) =>
          scheduledReport.id === action.payload.id
            ? action.payload
            : scheduledReport
        )
      )
      state.scheduledReportUpdateLoading = false
      state.scheduledReportUpdateError = null
      state.scheduledReportUpdateSuccess = true
    },
    createScheduledReportFailed(
      state,
      action: PayloadAction<{ message: string }>
    ) {
      state.scheduledReportCreateLoading = false
      state.scheduledReportCreateError = action.payload.message
      state.scheduledReportCreateSuccess = false
    },
    setScheduledReportUpdateLoading(state, action: PayloadAction<boolean>) {
      state.scheduledReportUpdateLoading = action.payload
    },
    updateScheduledReportFailed(
      state,
      action: PayloadAction<{ message: string }>
    ) {
      state.scheduledReportUpdateLoading = false
      state.scheduledReportUpdateError = action.payload.message
      state.scheduledReportUpdateSuccess = false
    },
    resetScheduledReportCreateState(state) {
      state.scheduledReportCreateLoading = false
      state.scheduledReportCreateError = null
      state.scheduledReportCreateSuccess = false
    },
    resetScheduledReportUpdateState(state) {
      state.scheduledReportUpdateLoading = false
      state.scheduledReportUpdateError = null
      state.scheduledReportUpdateSuccess = false
    },
  },
})

export const automatedReportingActions = {
  ...automatedReportingSlice.actions,
  ...automatedReportingAsyncActions,
}

export const automatedReportingReducer = automatedReportingSlice.reducer
